/**
 * Decision Log Page
 *
 * Full audit trail of all policy routing decisions with stats,
 * decisions-over-time chart, filters, and expandable decision cards.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, Badge, Button, cn } from '@respectly/ui';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import {
  FileSearch,
  ArrowLeft,
  Loader2,
  BarChart3,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  ChevronDown,
  ChevronUp,
  X,
  Filter,
  Globe,
  Clock,
  User,
  Building2,
  ShieldBan,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type {
  PREDecisionLog,
  PREChannel,
  PREDecision,
  MessagePurpose,
} from '@respectly/shared-types';
import {
  usePolicyStore,
  useDecisionStats,
} from '../../stores/usePolicyStore';
import { DecisionBadge } from '../../components/policy/DecisionBadge';
import { ConditionCodeList } from '../../components/policy/ConditionCodeList';
import { RiskFlagBadges } from '../../components/policy/RiskFlagBadges';
import { EligibilityFlagsDisplay } from '../../components/policy/EligibilityFlagsDisplay';
import { DecisionTraceExpander } from '../../components/policy/DecisionTraceExpander';
import { summarizeDecision } from '../../components/policy/decisionSummary';

interface TenantOption {
  id: string;
  name: string;
}

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const cardListVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// ============================================
// Helpers
// ============================================

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function pct(count: number, total: number): string {
  if (total === 0) return '0%';
  return Math.round((count / total) * 100) + '%';
}

/** Build time-series chart data from decision logs or generate mock data */
function buildChartData(
  logs: PREDecisionLog[]
): { date: string; ALLOW: number; CONDITIONAL: number; BLOCK: number }[] {
  if (logs.length < 2) {
    // Generate static 7-day mock dataset for visualization
    const data = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      data.push({
        date: formatShortDate(d),
        ALLOW: Math.floor(Math.random() * 8) + 3,
        CONDITIONAL: Math.floor(Math.random() * 5) + 1,
        BLOCK: Math.floor(Math.random() * 3),
      });
    }
    return data;
  }

  // Group logs by date
  const groups = new Map<string, { ALLOW: number; CONDITIONAL: number; BLOCK: number }>();
  logs.forEach((log) => {
    const key = formatShortDate(log.createdAt);
    const existing = groups.get(key) ?? { ALLOW: 0, CONDITIONAL: 0, BLOCK: 0 };
    if (log.decision === 'ALLOW') existing.ALLOW++;
    else if (log.decision === 'ALLOW_WITH_CONDITIONS') existing.CONDITIONAL++;
    else existing.BLOCK++;
    groups.set(key, existing);
  });

  return Array.from(groups.entries()).map(([date, counts]) => ({
    date,
    ...counts,
  }));
}

// ============================================
// Component
// ============================================

export function PolicyDecisionLog() {
  const decisionLogs = usePolicyStore((s) => s.decisionLogs);
  const isLoadingDecisionLogs = usePolicyStore((s) => s.isLoadingDecisionLogs);
  const loadDecisionLogs = usePolicyStore((s) => s.loadDecisionLogs);
  const decisionLogFilters = usePolicyStore((s) => s.decisionLogFilters);
  const setDecisionLogFilter = usePolicyStore((s) => s.setDecisionLogFilter);
  const clearDecisionLogFilters = usePolicyStore((s) => s.clearDecisionLogFilters);

  const stats = useDecisionStats();

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  useEffect(() => {
    loadDecisionLogs();
  }, [loadDecisionLogs]);

  // Lazy-load a small tenant list once for the filter dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = getApiClient();
        const response = await api.get<{ data: TenantOption[] }>(
          `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.LIST}?limit=100`,
        );
        if (!cancelled) {
          setTenants(
            (response.data ?? []).map((t) => ({ id: t.id, name: t.name })),
          );
        }
      } catch {
        // Tenant filter is optional — silent failure is fine.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const chartData = useMemo(() => buildChartData(decisionLogs), [decisionLogs]);

  const hasActiveFilters = !!(
    decisionLogFilters.channel ||
    decisionLogFilters.messagePurpose ||
    decisionLogFilters.decision ||
    decisionLogFilters.dateRange ||
    decisionLogFilters.tenantId ||
    decisionLogFilters.internalDncOnly
  );

  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <Link
            to="/settings/policy"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center">
            <FileSearch className="w-6 h-6 text-slate-600 dark:text-slate-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Decision Log
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Audit trail of all policy routing decisions
            </p>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoadingDecisionLogs ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
          </div>
        ) : (
          <motion.div
            variants={cardListVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6 max-w-6xl"
          >
            {/* Section 1 — Stats Bar */}
            <motion.div variants={cardItemVariants}>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* Total Evaluations */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {stats.total}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Total Evaluations
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* ALLOW */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {stats.allowed}
                          <span className="text-sm font-normal text-slate-400 ml-1">
                            {pct(stats.allowed, stats.total)}
                          </span>
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Allow
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* ALLOW_WITH_CONDITIONS */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {stats.conditional}
                          <span className="text-sm font-normal text-slate-400 ml-1">
                            {pct(stats.conditional, stats.total)}
                          </span>
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Conditional
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* BLOCK */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <ShieldX className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {stats.blocked}
                          <span className="text-sm font-normal text-slate-400 ml-1">
                            {pct(stats.blocked, stats.total)}
                          </span>
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Block
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            {/* Section 2 — Decisions Over Time Chart */}
            <motion.div variants={cardItemVariants}>
              <Card className="border-slate-200/60 dark:border-slate-700/60">
                <CardContent className="p-5">
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
                    Decisions Over Time
                  </h2>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={chartData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="currentColor"
                        className="text-slate-200 dark:text-slate-700"
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        stroke="currentColor"
                        className="text-slate-400"
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        stroke="currentColor"
                        className="text-slate-400"
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--tooltip-bg, #fff)',
                          borderColor: 'var(--tooltip-border, #e2e8f0)',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="ALLOW"
                        stackId="1"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.6}
                        name="Allow"
                      />
                      <Area
                        type="monotone"
                        dataKey="CONDITIONAL"
                        stackId="1"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.6}
                        name="Conditional"
                      />
                      <Area
                        type="monotone"
                        dataKey="BLOCK"
                        stackId="1"
                        stroke="#ef4444"
                        fill="#ef4444"
                        fillOpacity={0.6}
                        name="Block"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </motion.div>

            {/* Section 3 — Filters */}
            <motion.div variants={cardItemVariants}>
              <div className="flex items-center gap-3 flex-wrap">
                <Filter className="w-4 h-4 text-slate-400" />

                {tenants.length > 0 && (
                  <Select
                    value={decisionLogFilters.tenantId ?? 'all'}
                    onValueChange={(v) =>
                      setDecisionLogFilter('tenantId', v === 'all' ? undefined : v)
                    }
                  >
                    <SelectTrigger className="w-[180px] h-8 text-xs">
                      <SelectValue placeholder="Tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tenants</SelectItem>
                      {tenants.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Select
                  value={decisionLogFilters.channel ?? 'all'}
                  onValueChange={(v) =>
                    setDecisionLogFilter(
                      'channel',
                      v === 'all' ? undefined : (v as PREChannel)
                    )
                  }
                >
                  <SelectTrigger className="w-[130px] h-8 text-xs">
                    <SelectValue placeholder="Channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    <SelectItem value="VOICE">Voice</SelectItem>
                    <SelectItem value="SMS">SMS</SelectItem>
                    <SelectItem value="EMAIL">Email</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={decisionLogFilters.messagePurpose ?? 'all'}
                  onValueChange={(v) =>
                    setDecisionLogFilter(
                      'messagePurpose',
                      v === 'all' ? undefined : (v as MessagePurpose)
                    )
                  }
                >
                  <SelectTrigger className="w-[160px] h-8 text-xs">
                    <SelectValue placeholder="Purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Purposes</SelectItem>
                    <SelectItem value="MARKETING">Marketing</SelectItem>
                    <SelectItem value="INFORMATIONAL">Informational</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={decisionLogFilters.decision ?? 'all'}
                  onValueChange={(v) =>
                    setDecisionLogFilter(
                      'decision',
                      v === 'all' ? undefined : (v as PREDecision)
                    )
                  }
                >
                  <SelectTrigger className="w-[160px] h-8 text-xs">
                    <SelectValue placeholder="Decision" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Decisions</SelectItem>
                    <SelectItem value="ALLOW">Allow</SelectItem>
                    <SelectItem value="ALLOW_WITH_CONDITIONS">Conditional</SelectItem>
                    <SelectItem value="BLOCK">Block</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={decisionLogFilters.dateRange ?? 'all'}
                  onValueChange={(v) =>
                    setDecisionLogFilter(
                      'dateRange',
                      v === 'all' ? undefined : (v as 'all' | '24h' | '7days' | '30days' | '90days')
                    )
                  }
                >
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="24h">Last 24h</SelectItem>
                    <SelectItem value="7days">Last 7 days</SelectItem>
                    <SelectItem value="30days">Last 30 days</SelectItem>
                    <SelectItem value="90days">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>

                <button
                  type="button"
                  onClick={() =>
                    setDecisionLogFilter(
                      'internalDncOnly',
                      decisionLogFilters.internalDncOnly ? undefined : true,
                    )
                  }
                  className={cn(
                    'h-8 px-2.5 inline-flex items-center gap-1.5 rounded-md text-xs font-medium transition-colors border',
                    decisionLogFilters.internalDncOnly
                      ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                      : 'bg-transparent text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800',
                  )}
                >
                  <ShieldBan className="w-3 h-3" />
                  Internal DNC only
                </button>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDecisionLogFilters}
                    className="h-8 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </motion.div>

            {/* Section 4 — Decision Table */}
            <motion.div variants={cardItemVariants} className="space-y-3">
              <div className="flex items-center gap-2">
                <FileSearch className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Decisions
                </h2>
                <Badge variant="secondary" className="text-[10px] tabular-nums">
                  {decisionLogs.length}
                </Badge>
              </div>

              {decisionLogs.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                      <FileSearch className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                      No decisions found
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {hasActiveFilters
                        ? 'No decisions match your filters. Try adjusting or clearing them.'
                        : 'Decision logs will appear here as the policy engine evaluates messages.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <motion.div
                  variants={cardListVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-3"
                >
                  {decisionLogs.map((log) => {
                    const isExpanded = expandedRows.has(log.id);
                    return (
                      <motion.div key={log.id} variants={cardItemVariants}>
                        <DecisionLogCard
                          log={log}
                          isExpanded={isExpanded}
                          onToggle={() => toggleRow(log.id)}
                        />
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// Decision Log Card (inline sub-component)
// ============================================

interface DecisionLogCardProps {
  log: PREDecisionLog;
  isExpanded: boolean;
  onToggle: () => void;
}

function DecisionLogCard({ log, isExpanded, onToggle }: DecisionLogCardProps) {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardContent className="p-0">
        {/* Top Row — Summary (clickable) */}
        <button
          onClick={onToggle}
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors rounded-xl"
        >
          {/* Tenant ID */}
          <span
            className="font-mono text-[10px] text-slate-500 dark:text-slate-500 truncate max-w-[100px] flex-shrink-0"
            title={log.tenantId}
          >
            {log.tenantId.slice(0, 8)}...
          </span>

          {/* Channel */}
          <Badge variant="secondary" className="text-[10px] flex-shrink-0">
            {log.channel}
          </Badge>

          {/* Purpose */}
          <Badge
            variant={log.messagePurpose === 'MARKETING' ? 'info' : 'success'}
            className="text-[10px] flex-shrink-0"
          >
            {log.messagePurpose}
          </Badge>

          {/* Jurisdiction */}
          <Badge variant="outline" className="text-[10px] flex-shrink-0">
            <Globe className="w-3 h-3 mr-0.5" />
            {log.jurisdictionCode}
          </Badge>

          {/* Decision */}
          <DecisionBadge decision={log.decision} />

          {/* Internal DNC marker */}
          {log.internalDncSuppressed && (
            <Badge
              className="bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-0 text-[10px] flex-shrink-0"
              title={log.internalDncReason ?? 'Provider call suppressed by Internal DNC'}
            >
              <ShieldBan className="w-3 h-3 mr-0.5" />
              Internal DNC
            </Badge>
          )}

          {/* Eligibility */}
          <Badge
            variant={log.eligibility === 'ELIGIBLE' ? 'success' : 'error'}
            className="text-[10px] flex-shrink-0"
          >
            {log.eligibility === 'ELIGIBLE' ? 'Eligible' : 'Not Eligible'}
          </Badge>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Timestamp */}
          <span className="text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0 hidden sm:block">
            {formatDate(log.createdAt)}
          </span>

          {/* Expand icon */}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
          )}
        </button>

        {/* Expanded Detail */}
        {isExpanded && (
          <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4 bg-slate-50/50 dark:bg-slate-800/30">
            {/* Plain-English summary */}
            <PlainEnglishSummary log={log} />

            {/* Input Summary */}
            <div>
              <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                Input
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
                {(log.input.recipient?.addressing?.phone || log.input.recipient?.addressing?.email) && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Contact</span>
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      {log.input.recipient.addressing.phone ?? log.input.recipient.addressing.email ?? '—'}
                    </p>
                  </div>
                )}
                {log.input.recipient?.country && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Country</span>
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      {log.input.recipient.country}
                    </p>
                  </div>
                )}
                {log.input.recipient?.b2bOrB2c && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Type</span>
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      {log.input.recipient.b2bOrB2c}
                    </p>
                  </div>
                )}
                {log.input.localTimeAtRecipient && (
                  <div className="flex items-start gap-1">
                    <Clock className="w-3 h-3 text-slate-400 mt-0.5" />
                    <div>
                      <span className="text-slate-500 dark:text-slate-400">Local Time</span>
                      <p className="font-medium text-slate-700 dark:text-slate-300">
                        {formatDate(log.input.localTimeAtRecipient)}
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-slate-500 dark:text-slate-400">DNC Status</span>
                  <p className="font-medium text-slate-700 dark:text-slate-300">
                    {log.input.internalDncStatus}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Registry Status</span>
                  <p className="font-medium text-slate-700 dark:text-slate-300">
                    {log.input.nationalRegistryStatus}
                  </p>
                </div>
                {log.input.tenantId && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Tenant</span>
                    <p className="font-mono text-[10px] text-slate-700 dark:text-slate-300 truncate">
                      {log.input.tenantId}
                    </p>
                  </div>
                )}
                {log.input.campaignId && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Campaign</span>
                    <p className="font-mono text-[10px] text-slate-700 dark:text-slate-300 truncate">
                      {log.input.campaignId}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Eligibility Flags */}
            {log.input.contactEligibility && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  Eligibility Flags
                </h4>
                <EligibilityFlagsDisplay
                  flags={log.input.contactEligibility}
                  compact
                />
              </div>
            )}

            {/* Conditions */}
            {log.output.conditions.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Conditions ({log.output.conditions.length})
                </h4>
                <ConditionCodeList
                  conditions={log.output.conditions.map((c) => ({
                    code: c.code,
                    params: c.params ?? undefined,
                    description: c.description ?? undefined,
                  }))}
                />
              </div>
            )}

            {/* Risk Flags */}
            {log.output.riskFlags.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Risk Flags ({log.output.riskFlags.length})
                </h4>
                <RiskFlagBadges
                  flags={log.output.riskFlags.map((f) => ({
                    code: f.code,
                    description: f.description ?? undefined,
                  }))}
                />
              </div>
            )}

            {/* Remediation */}
            {log.output.remediation.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Remediation ({log.output.remediation.length})
                </h4>
                <ConditionCodeList
                  conditions={log.output.remediation.map((r) => ({
                    code: r.code,
                    params: r.params ?? undefined,
                    description: r.description ?? undefined,
                  }))}
                />
              </div>
            )}

            {/* Trace */}
            {(log.output.trace.length > 0 || (log.output.consentCapture?.length ?? 0) > 0) && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Rule Trace
                </h4>
                <DecisionTraceExpander
                  trace={log.output.trace}
                  consentCapture={log.output.consentCapture}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Plain-English Summary Banner
// ============================================

function PlainEnglishSummary({ log }: { log: PREDecisionLog }) {
  const summary = summarizeDecision(log);
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        summary.tone === 'success' &&
          'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-900/40',
        summary.tone === 'warning' &&
          'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/40',
        summary.tone === 'error' &&
          'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/40',
      )}
    >
      <p
        className={cn(
          'text-[10px] font-bold uppercase tracking-wide mb-1',
          summary.tone === 'success' && 'text-emerald-700 dark:text-emerald-400',
          summary.tone === 'warning' && 'text-amber-700 dark:text-amber-400',
          summary.tone === 'error' && 'text-red-700 dark:text-red-400',
        )}
      >
        PRE Verdict — {summary.verdict}
        {log.internalDncSuppressed && (
          <span className="ml-2 inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-semibold normal-case tracking-normal">
            <ShieldBan className="w-3 h-3" />
            no SMS sent (Internal DNC)
          </span>
        )}
      </p>
      <p className="text-sm text-slate-700 dark:text-slate-300">{summary.detail}</p>
    </div>
  );
}
